import React, { useState } from "react"
import {
  Box, Flex, Stack, Text,
} from "@chakra-ui/react"
import ModalForm from "components/dialogs/ReactHookForm/ModalForm"
import Button from "components/Buttons/Button"
import CopyableLink from "components/elements/CopyableLink"
import Confirm from "components/dialogs/Confirm/Confirm"
import Link from "components/elements/Link"
import TextField from "components/ReactHookForm/TextField"
import { asUsd, asDate } from "utilities/strings"
import { accountPlanPath } from "utilities/routes"
import { useLeadScans } from "queries/events"
import ExportLeads from "modals/EditExhibitor/ExportLeads"

type Props = {
  eventId: string
  licenseFee: number
  exhibitor: {
    email: string
    name?: string
    sourceLabel: "Organizer-Paid" | "Exhibitor-Paid"
    statusLabel: "Active" | "Invited"
    activatedAt?: string
    invitedAt?: string
    licenseId?: number
    inviteUrl?: string
  }
  onDelete: () => void | Promise<void>
  onResend?: () => void | Promise<void>
  onUpdateName?: (name: string) => void | Promise<void>
  onHide: () => void
}

const EditExhibitor = ({
  eventId, licenseFee, exhibitor, onDelete, onResend, onUpdateName, onHide,
}: Props) => {
  const {
    email, name, sourceLabel, statusLabel, activatedAt, invitedAt, licenseId, inviteUrl,
  } = exhibitor
  const isActive = statusLabel === "Active"
  const isOrganizerPaid = sourceLabel === "Organizer-Paid"
  const { data: leadScans } = useLeadScans(eventId, isActive ? licenseId : undefined)
  const totalLeadScans = leadScans ? leadScans.hot + leadScans.warm + leadScans.cold : 0
  const [isResending, setIsResending] = useState(false)
  const [showExportLeads, setShowExportLeads] = useState(false)
  // Kept outside ModalForm's own state so an in-progress (unsaved) name edit survives
  // swapping to the Export CSV view and back, instead of silently resetting.
  const [draftName, setDraftName] = useState(name || "")

  const handleResend = async () => {
    setIsResending(true)
    try {
      await onResend?.()
    } finally {
      setIsResending(false)
    }
  }

  const onSubmit = (values) => Promise.resolve(onUpdateName?.(values.name)).then(onHide)

  if (showExportLeads && licenseId != null) {
    return (
      <ExportLeads
        eventId={eventId}
        eventAccessId={licenseId}
        onBack={() => setShowExportLeads(false)}
        onHide={() => setShowExportLeads(false)}
      />
    )
  }

  const deleteConfirmationText = isActive
    ? `This will revoke ${email}'s Lead Retrieval access to this event and release the license back to the event. Their Diobox account will not be affected.`
    : isOrganizerPaid
      ? `This will cancel the pending invite sent to ${email} and release the license back to the event. They will no longer be able to use their invite link to activate it.`
      : `This will cancel the pending invite sent to ${email}. They will no longer be able to use it to activate a license.`

  const deleteAction = isActive && !isOrganizerPaid ? (
    <Button variant="outline" colorScheme="button.danger" isDisabled>Delete Exhibitor</Button>
  ) : (
    <Confirm
      confirmationTitle="Delete Exhibitor"
      confirmationText={deleteConfirmationText}
      confirmButtonLabel="Delete Exhibitor"
      confirmButtonColorScheme="button.danger"
      onClickConfirm={onDelete}
    >
      <Button variant="outline" colorScheme="button.danger">Delete Exhibitor</Button>
    </Confirm>
  )

  return (
    <ModalForm
      title="Edit Exhibitor"
      onClose={onHide}
      initialValues={{ name: draftName, email }}
      onSubmit={onSubmit}
      submitText="Save"
      disableSubmitWhenNotDirty
      footerLeft={deleteAction}
      size="lg"
    >
      {({ control }) => (
        <Stack spacing={8}>
          <Flex gap={4}>
            {isActive && (
              <Box flex={1}>
                <TextField
                  name="name"
                  control={control}
                  onChange={(e) => setDraftName(e.target.value)}
                  label="Company Name"
                  labelWeight="semibold"
                  variant="outline"
                  size="sm"
                  h={8}
                  vertical
                  mb={0}
                />
              </Box>
            )}
            <Box flex={1}>
              <TextField
                name="email"
                control={control}
                label="Email"
                labelWeight="semibold"
                variant="outline"
                size="sm"
                h={8}
                vertical
                mb={0}
                readonly
              />
            </Box>
          </Flex>

          <Box>
            <Text fontWeight="semibold" fontSize="lg" mb={3}>License</Text>
            <Box borderWidth={1} borderRadius="md">
              <Flex justifyContent="space-between" px={4} py={3} borderBottomWidth={1}>
                <Text color="gray.600">Status</Text>
                <Box textAlign="right">
                  <Text fontWeight="bold">{statusLabel}</Text>
                  {isActive && activatedAt && (
                    <Text>Activated {asDate(activatedAt, "PP")}</Text>
                  )}
                  {!isActive && invitedAt && (
                    <Text>Invitation sent: {asDate(invitedAt, "PP")}</Text>
                  )}
                </Box>
              </Flex>
              {!isActive && (
                <Flex justifyContent="space-between" alignItems="center" px={4} py={3} borderBottomWidth={1}>
                  <Text color="gray.600">Invitation</Text>
                  <Button variant="outline" size="sm" onClick={handleResend} isLoading={isResending}>
                    Resend Invite
                  </Button>
                </Flex>
              )}
              {!isActive && inviteUrl && (
                <Box px={4} py={3} borderBottomWidth={1}>
                  <CopyableLink
                    label={isOrganizerPaid ? "Invite Link" : "Purchase Link"}
                    value={inviteUrl}
                    helperText={isOrganizerPaid
                      ? "Share this link with the exhibitor to activate the license. It is unique to this exhibitor."
                      : "Share this link with the exhibitor to purchase their own license."}
                  />
                </Box>
              )}
              <Flex justifyContent="space-between" alignItems="center" px={4} py={3}>
                <Text color="gray.600">License</Text>
                <Box textAlign="right">
                  <Text fontWeight="bold">
                    {sourceLabel} / {asUsd(licenseFee)}
                  </Text>
                  {isOrganizerPaid && (
                    <Text fontSize="sm" color="gray.600">
                      Receipt available in the{" "}
                      <Link href={accountPlanPath()} isExternal>Billing Portal</Link>
                    </Text>
                  )}
                  {!isOrganizerPaid && isActive && (
                    <Button variant="outline" size="sm" mt={1} onClick={handleResend} isLoading={isResending}>
                      Email Receipt
                    </Button>
                  )}
                </Box>
              </Flex>
            </Box>
          </Box>

          {isActive && (
            <Box>
              <Text fontWeight="semibold" fontSize="lg" mb={3}>Lead Scans</Text>
              <Box borderWidth={1} borderRadius="md" overflow="hidden">
                <Flex>
                  <Box flex={1} px={4} py={4} borderRightWidth={1}>
                    <Flex align="center" gap={2}>
                      <Box w={2} h={2} borderRadius="full" bg="orange.500" />
                      <Text color="gray.600">Hot</Text>
                    </Flex>
                    <Text fontWeight="bold" fontSize="xl">{leadScans?.hot ?? "–"}</Text>
                  </Box>
                  <Box flex={1} px={4} py={4} borderRightWidth={1}>
                    <Flex align="center" gap={2}>
                      <Box w={2} h={2} borderRadius="full" bg="yellow.400" />
                      <Text color="gray.600">Warm</Text>
                    </Flex>
                    <Text fontWeight="bold" fontSize="xl">{leadScans?.warm ?? "–"}</Text>
                  </Box>
                  <Box flex={1} px={4} py={4}>
                    <Flex align="center" gap={2}>
                      <Box w={2} h={2} borderRadius="full" bg="blue.300" />
                      <Text color="gray.600">Cold</Text>
                    </Flex>
                    <Text fontWeight="bold" fontSize="xl">{leadScans?.cold ?? "–"}</Text>
                  </Box>
                </Flex>
                <Flex justifyContent="space-between" alignItems="center" px={4} py={3} bg="blue.50" borderTopWidth={1}>
                  <Text>
                    Total Lead Scans: {" "}
                    <Text as="span" fontWeight="bold">
                      {leadScans ? totalLeadScans : "–"}
                    </Text>
                  </Text>
                  <Button
                    variant="outline"
                    size="sm"
                    bg="white"
                    isDisabled={totalLeadScans === 0}
                    onClick={() => setShowExportLeads(true)}
                  >
                    Export CSV
                  </Button>
                </Flex>
              </Box>
            </Box>
          )}
        </Stack>
      )}
    </ModalForm>
  )
}

export default EditExhibitor
